import React, { createContext, useMemo, useContext } from "react";
import PropTypes from "prop-types";

export const ConfigContext = createContext();

export function useConfig() {
  return useContext(ConfigContext);
}

/**
 * Détecte la configuration pour le contexte
 * @returns {object}
 */
export function detectConfig() {
  // Chargement du fichier de configuration de l'instance
  if (!process.env.REACT_APP_CONFIG) {
    throw new Error(
      "Config file name is not defined in REACT_APP_CONFIG env variable.",
    );
  }
  const config = require(`config/${process.env.REACT_APP_CONFIG}.json`);

  // Paramètres de configuration à fournir dans le contexte
  const contextConfig = {
    environment: null, // Nom de l'environnement utilisé
    apiUrl: null, // URL du back-end
    frontUrl: null, // URL du front
    simulatorUrl: null, // URL du simulateur intégré
    isCustomerUrl: null, // True lorsque l'application est accédée en tant qu'espace client
    isManagerUrl: null, // True lorsque l'application est accédée en tant qu'espace gestionnaire
    isPartnerUrl: null, // True lorsque l'application est accédée en tant qu'espace partenaire
  };

  // Détection de la configuration en fonction de l'URL
  for (const environment of Object.keys(config)) {
    const envConfig = config[environment];

    const customerUrls = Array.isArray(envConfig.customerUrl)
      ? [...envConfig.customerUrl]
      : [envConfig.customerUrl];
    const managerUrls = Array.isArray(envConfig.managerUrl)
      ? [...envConfig.managerUrl]
      : [envConfig.managerUrl];
    const partnerUrls = Array.isArray(envConfig.partnerUrl)
      ? [...envConfig.partnerUrl]
      : [envConfig.partnerUrl];

    const customerUrl = customerUrls.find((url) =>
      window.location.href.includes(url),
    );
    const managerUrl = managerUrls.find((url) =>
      window.location.href.includes(url),
    );
    const partnerUrl = partnerUrls.find((url) =>
      window.location.href.includes(url),
    );

    if (customerUrl || partnerUrl || managerUrl) {
      contextConfig.environment = environment;
      contextConfig.apiUrl = envConfig.apiUrl;
      contextConfig.frontUrl = customerUrl || partnerUrl || managerUrl;
      contextConfig.simulatorUrl = envConfig.simulatorUrl;
      contextConfig.isCustomerUrl = !!customerUrl;
      contextConfig.isManagerUrl = !!managerUrl;
      contextConfig.isPartnerUrl = !!partnerUrl;
      contextConfig.customerUrl = customerUrls[0];
      contextConfig.managerUrl = managerUrls[0];
      contextConfig.partnerUrl = partnerUrls[0];
    }
  }

  // Surcharge de la configuration à l'aide des variables d'environnement
  if (process.env.REACT_APP_API_ENDPOINT) {
    contextConfig.environment = "custom";
    contextConfig.apiUrl = process.env.REACT_APP_API_ENDPOINT;
  }
  if (
    process.env.REACT_APP_MANAGER_URL &&
    window.location.href.includes(process.env.REACT_APP_MANAGER_URL)
  ) {
    contextConfig.environment = "custom";
    contextConfig.frontUrl = process.env.REACT_APP_MANAGER_URL;
    contextConfig.managerUrl = process.env.REACT_APP_MANAGER_URL;
    contextConfig.isCustomerUrl = false;
    contextConfig.isManagerUrl = true;
    contextConfig.isPartnerUrl = false;
  }
  if (
    process.env.REACT_APP_CUSTOMER_URL &&
    window.location.href.includes(process.env.REACT_APP_CUSTOMER_URL)
  ) {
    contextConfig.environment = "custom";
    contextConfig.frontUrl = process.env.REACT_APP_CUSTOMER_URL;
    contextConfig.customerUrl = process.env.REACT_APP_CUSTOMER_URL;
    contextConfig.isCustomerUrl = true;
    contextConfig.isManagerUrl = false;
    contextConfig.isPartnerUrl = false;
  }
  if (
    process.env.REACT_APP_PARTNER_URL &&
    window.location.href.includes(process.env.REACT_APP_PARTNER_URL)
  ) {
    contextConfig.environment = "custom";
    contextConfig.frontUrl = process.env.REACT_APP_PARTNER_URL;
    contextConfig.partnerUrl = process.env.REACT_APP_PARTNER_URL;
    contextConfig.isCustomerUrl = false;
    contextConfig.isManagerUrl = false;
    contextConfig.isPartnerUrl = true;
  }
  if (process.env.REACT_APP_SIMULATOR_URL) {
    contextConfig.environment = "custom";
    contextConfig.simulatorUrl = process.env.REACT_APP_SIMULATOR_URL;
  }

  // Déclenche une erreur au cas où l'environnement n'a pas pu être détecté
  // et redirige l'utilisateur vers le site de Sorégies
  if (
    !contextConfig.environment ||
    !contextConfig.apiUrl ||
    !contextConfig.frontUrl ||
    !contextConfig.simulatorUrl
  ) {
    window.location.href = "https://soregies.fr";
    throw new Error("Environnement invalide");
  }

  // Renvoie la configuration pour le contexte
  return contextConfig;
}

/**
 * Fournit un contexte avec la configuration de l'instance de l'application.
 */
export default function ConfigProvider({ children }) {
  const contextValue = useMemo(() => detectConfig(), []);
  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
}

ConfigProvider.propTypes = {
  children: PropTypes.any.isRequired,
};
